import type { SimulatorArea } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { FieldSwitcher, Form, FormField, genDataField } from 'components/formKit'
import { Tooltip } from 'components/uiKit/Dropdown'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import { useSimulatorContext } from '../SimulatorContext'

type AreaSettingsValues = {
  showClickableArea: boolean
  rightClickToAction: boolean
  name: string
}

const genField = genDataField<AreaSettingsValues>()

const rightClickToActionField = genField({
  as: FieldSwitcher,
  name: 'rightClickToAction',
  label: t('elements.simulator.settings.rightClickToAction'),
  layout: 'horizontal',
  defaultValue: false,
  size: KitSize.S,
})

interface IAreaSettingsProps {
  area: SimulatorArea
}

const AreaSettings = ({ area }: IAreaSettingsProps) => {
  const onChange = useSimulatorContext((c) => c.onChange)
  const value = useSimulatorContext((c) => c.value)

  const showClickableAreaField = useMemo(
    () =>
      genField({
        as: FieldSwitcher,
        name: 'showClickableArea',
        label: (
          <Tooltip overlay={t('elements.simulator.areaSettings.hint')}>
            {t('elements.simulator.settings.showClickableArea')}
          </Tooltip>
        ),
        layout: 'horizontal',
        defaultValue: false,
        disabled: !value.showArea,
        size: KitSize.S,
      }),
    [value.showArea],
  )

  const onFinish = (values: AreaSettingsValues) => {
    onChange?.({
      ...value,
      items: value.items.map((item) => {
        if (!item.areas) {
          return item
        }
        const updatedAreas = item.areas.map((a) => {
          if (a.id === area.id) {
            return { ...a, ...values }
          }
          return a
        })
        return { ...item, areas: updatedAreas }
      }),
    })
  }

  const initialValues: AreaSettingsValues = {
    showClickableArea: area.showClickableArea ?? value.showArea ?? false,
    rightClickToAction: area.rightClickToAction ?? false,
    name: area.name ?? '',
  }

  return (
    <div style={{ width: 280 }}>
      <Form
        initialValues={initialValues}
        layout='vertical'
        name='areaSettings'
        onChangeValues={onFinish}
      >
        <Padding bottom gap sides top>
          <FormField config={showClickableAreaField} />
          <FormField config={rightClickToActionField} />
        </Padding>
      </Form>
    </div>
  )
}

export default AreaSettings
